// frontend/src/components/EngagementRateCalculator.js
//
// The engagement rate calculator embedded on /tools/engagement-rate-calculator/*.
//
// WHY IT IS BUILT THIS WAY. The SERP for `engagement rate calculator` is not a
// listicle — every result is a working single-purpose tool (DashThis, Omni
// Calculator, GRIN, Social Insider, Nativz, 1stCollab). So a tool is the only
// format that can rank, and the differentiator has to be inside the tool.
//
// THE DIFFERENTIATOR: every platform defines engagement rate with a DIFFERENT
// formula. Instagram counts likes + comments; TikTok also counts shares and
// saves; Facebook counts reactions; X counts reposts. Most competing
// calculators apply one formula to every platform, which produces a number that
// does not match what the platform — or a brand partner — would quote. This one
// uses the correct formula per platform and shows its working.
//
// Pure client-side arithmetic: no API call, no cost, no rate limit, works
// instantly. Deliberately unlike the best-time tool, which needs a model.
//
// BENCHMARKS ARE SOURCED, NOT INVENTED. Figures come from Socialinsider's
// analysis of 70 million posts (January 2024 – December 2025). Where that study
// has no figure — LinkedIn, YouTube — this shows no benchmark rather than a
// guess. See BENCHMARK_SOURCE below; re-verify the date before republishing.

import React, { useState, useMemo } from 'react'
import { Row, Col, Form, Button, Card } from 'react-bootstrap'
import { Calculator, ArrowRepeat, InfoCircle } from 'react-bootstrap-icons'

export const BENCHMARK_SOURCE = {
  name: 'Socialinsider',
  detail: '70 million posts, January 2024 – December 2025',
  url: 'https://www.socialinsider.io/social-media-benchmarks',
}

// fields: which interactions that platform's own definition counts.
// benchmark: median engagement rate by follower, or null where unsourced.
const PLATFORMS = {
  instagram: {
    label: 'Instagram', benchmark: 0.48,
    fields: [['likes', 'Likes'], ['comments', 'Comments']],
    note: 'Instagram engagement rate counts likes and comments against followers.',
  },
  tiktok: {
    label: 'TikTok', benchmark: 2.60,
    fields: [['likes', 'Likes'], ['comments', 'Comments'], ['shares', 'Shares'], ['saves', 'Saves']],
    note: 'TikTok counts shares and saves too, which is a large part of why its rates run higher.',
  },
  facebook: {
    label: 'Facebook', benchmark: 0.15,
    fields: [['likes', 'Reactions'], ['comments', 'Comments'], ['shares', 'Shares']],
    note: 'Facebook counts all reactions, not just likes.',
  },
  x: {
    label: 'X (Twitter)', benchmark: 0.12,
    fields: [['likes', 'Likes'], ['shares', 'Reposts']],
    note: 'X counts likes and reposts against followers.',
  },
  linkedin: {
    label: 'LinkedIn', benchmark: null,
    fields: [['likes', 'Reactions'], ['comments', 'Comments'], ['shares', 'Reposts']],
    note: 'No median for LinkedIn in the source study, so no benchmark is shown rather than a guess.',
  },
  youtube: {
    label: 'YouTube', benchmark: null,
    fields: [['likes', 'Likes'], ['comments', 'Comments']],
    note: 'YouTube engagement is usually measured against views rather than subscribers.',
  },
}

const num = v => {
  const n = parseFloat(String(v).replace(/[, ]/g, ''))
  return Number.isFinite(n) && n >= 0 ? n : 0
}
const fmt = n => n >= 100 ? n.toFixed(0) : n >= 10 ? n.toFixed(1) : n.toFixed(2)

export default function EngagementRateCalculator ({ platform = 'instagram' }) {
  const [p, setP] = useState(PLATFORMS[platform] ? platform : 'instagram')
  const [followers, setFollowers] = useState('')
  const [reach, setReach] = useState('')
  const [counts, setCounts] = useState({})

  const cfg = PLATFORMS[p]
  const set = (k, v) => setCounts(c => ({ ...c, [k]: v }))

  const result = useMemo(() => {
    const f = num(followers)
    const r = num(reach)
    const total = cfg.fields.reduce((s, [k]) => s + num(counts[k]), 0)
    if (!total || (!f && !r)) return null
    return {
      total,
      byFollower: f ? (total / f) * 100 : null,
      byReach:    r ? (total / r) * 100 : null,
    }
  }, [followers, reach, counts, cfg])

  const reset = () => { setFollowers(''); setReach(''); setCounts({}) }

  const verdict = er => {
    if (cfg.benchmark == null || er == null) return null
    const ratio = er / cfg.benchmark
    if (ratio >= 2)   return { text: `Roughly ${fmt(ratio)}× the ${cfg.label} median`, tone: 'success' }
    if (ratio >= 1)   return { text: `Above the ${cfg.label} median of ${cfg.benchmark}%`, tone: 'success' }
    if (ratio >= 0.5) return { text: `A little below the ${cfg.label} median of ${cfg.benchmark}%`, tone: 'warning' }
    return { text: `Below the ${cfg.label} median of ${cfg.benchmark}%`, tone: 'danger' }
  }

  const v = result ? verdict(result.byFollower) : null
  const TONE = { success: '#198754', warning: '#b8860b', danger: '#b02a37' }

  return (
    <div className='p-4 p-md-5 rounded-3 shadow-card' style={{ background: '#fff', border: '1px solid #e5e7eb' }}>
      <h2 className='fw-bold h4 mb-2'>
        <Calculator className='text-primary me-2' />
        Engagement rate calculator
      </h2>
      <p className='text-muted mb-4'>
        Each platform counts engagement differently, so a single formula gives you a number
        nobody else will recognise. Pick your platform and this uses that platform's own
        definition.
      </p>

      <Form onSubmit={e => e.preventDefault()}>
        <Row className='g-3 mb-3'>
          <Col md={4}>
            <Form.Label className='fw-semibold small'>Platform</Form.Label>
            <Form.Select value={p} onChange={e => { setP(e.target.value); setCounts({}) }}>
              {Object.entries(PLATFORMS).map(([k, c]) => <option key={k} value={k}>{c.label}</option>)}
            </Form.Select>
          </Col>
          <Col md={4}>
            <Form.Label className='fw-semibold small'>
              {p === 'youtube' ? 'Subscribers' : 'Followers'}
            </Form.Label>
            <Form.Control inputMode='numeric' placeholder='e.g. 4200'
                          value={followers} onChange={e => setFollowers(e.target.value)} />
          </Col>
          <Col md={4}>
            <Form.Label className='fw-semibold small'>
              {p === 'youtube' || p === 'tiktok' ? 'Views' : 'Reach'} <span className='text-muted fw-normal'>(optional)</span>
            </Form.Label>
            <Form.Control inputMode='numeric' placeholder='e.g. 11800'
                          value={reach} onChange={e => setReach(e.target.value)} />
          </Col>
        </Row>

        <Form.Label className='fw-semibold small'>
          Engagement on the post {cfg.fields.length > 2 && <span className='text-muted fw-normal'>— {cfg.label} counts all of these</span>}
        </Form.Label>
        <Row className='g-3 mb-3'>
          {cfg.fields.map(([k, label]) => (
            <Col xs={6} md={3} key={k}>
              <Form.Control inputMode='numeric' placeholder={label} aria-label={label}
                            value={counts[k] || ''} onChange={e => set(k, e.target.value)} />
              <Form.Text className='text-muted' style={{ fontSize: 12 }}>{label}</Form.Text>
            </Col>
          ))}
        </Row>

        {result && (
          <Button variant='outline-secondary' size='sm' onClick={reset} className='mb-2'>
            <ArrowRepeat className='me-1' />Clear
          </Button>
        )}
      </Form>

      {!result && (
        <p className='text-muted small mb-0'>
          <InfoCircle className='me-1' />
          Enter your {p === 'youtube' ? 'subscribers' : 'followers'} and the engagement on one post.
          Nothing is sent anywhere — this runs in your browser.
        </p>
      )}

      {result && (
        <div className='mt-4'>
          <Row className='g-3'>
            {result.byFollower != null && (
              <Col md={result.byReach != null ? 6 : 12}>
                <Card className='h-100' style={{ border: '1px solid #e5e7eb' }}>
                  <Card.Body>
                    <div className='text-muted small text-uppercase' style={{ letterSpacing: '.04em' }}>
                      By {p === 'youtube' ? 'subscriber' : 'follower'}
                    </div>
                    <div className='fw-bold text-primary' style={{ fontSize: '2.5rem', lineHeight: 1.1 }}>
                      {fmt(result.byFollower)}%
                    </div>
                    {v && <div className='small fw-semibold' style={{ color: TONE[v.tone] }}>{v.text}</div>}
                  </Card.Body>
                </Card>
              </Col>
            )}
            {result.byReach != null && (
              <Col md={result.byFollower != null ? 6 : 12}>
                <Card className='h-100' style={{ border: '1px solid #e5e7eb' }}>
                  <Card.Body>
                    <div className='text-muted small text-uppercase' style={{ letterSpacing: '.04em' }}>
                      By {p === 'youtube' || p === 'tiktok' ? 'view' : 'reach'}
                    </div>
                    <div className='fw-bold' style={{ fontSize: '2.5rem', lineHeight: 1.1 }}>
                      {fmt(result.byReach)}%
                    </div>
                    <div className='small text-muted'>
                      The number most brand partners ask for.
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>

          {/* Show the working — the competing calculators return a number and no formula. */}
          <div className='mt-3 p-3 rounded-3' style={{ background: '#f8f9fa', border: '1px solid #e5e7eb' }}>
            <div className='small text-muted mb-1'>How that was worked out</div>
            <code className='small'>
              ({cfg.fields.map(([, l]) => l.toLowerCase()).join(' + ')}) ÷ {p === 'youtube' ? 'subscribers' : 'followers'} × 100
              {' = '}({result.total.toLocaleString()} ÷ {num(followers).toLocaleString()}) × 100
              {' = '}{fmt(result.byFollower ?? 0)}%
            </code>
            <div className='small text-muted mt-2 mb-0'>{cfg.note}</div>
          </div>

          {cfg.benchmark != null && (
            <p className='text-muted small mt-2'>
              Median for {cfg.label} is {cfg.benchmark}%, per{' '}
              <a href={BENCHMARK_SOURCE.url} target='_blank' rel='noopener noreferrer nofollow'>
                {BENCHMARK_SOURCE.name}
              </a>{' '}({BENCHMARK_SOURCE.detail}). Medians move — treat it as a reference point, not a target.
            </p>
          )}

          {/* The bridge: they now have a number and want it to go up. */}
          <div className='p-4 rounded-3 mt-4' style={{ background: 'linear-gradient(135deg,#1d4ed8,#2563eb)', color: '#fff' }}>
            <h3 className='fw-bold h5 mb-2'>The two things that move this number</h3>
            <p className='mb-3' style={{ color: 'rgba(255,255,255,.88)' }}>
              Posting consistently, and posting when your audience is actually awake. At Greatly
              Social you can schedule a whole week in one sitting across eight networks, so
              neither depends on you being free at the right moment.
            </p>
            <Button variant='light' href='/register' className='fw-semibold me-2 mb-2'>
              Start using Greatly Social free
            </Button>
            <Button variant='outline-light' href='/best-time-to-post' className='mb-2'>
              Find your best times
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
